import * as I18n from 'i18n-js';
import * as React from 'react';

import Currency from '@reverbdotcom/commons/dist/currency';

import FormGroupWithCheckbox from '@reverbdotcom/commons/dist/components/form_group_with_checkbox';
import FormGroupWithInput from '@reverbdotcom/commons/dist/components/form_group_with_input';
import { core_apimessages_Money } from '../../schemas/rql';

const EVERYWHERE_ELSE_CODE = 'XX';

const STANDARD_RATE = 'standardRate';
const COMBINED_RATE = 'combinedRate';
const EXPEDITED_RATE = 'expeditedRate';

// This component currently renders inputs within a rails form, and respects the input names expected by the backend.
// Once shipping profiles can be submitted via a mutation, this naming can be stripped out/simplified.
const INPUT_NAMES = {
  [STANDARD_RATE]: 'shipping_profile[shipping][rates][][rate]',
  [COMBINED_RATE]: 'shipping_profile[shipping][rates][][incremental_rate]',
  [EXPEDITED_RATE]: 'shipping_profile[shipping][rates][][expedited_rate]',
  regionType: 'shipping_profile[shipping][rates][][region_type]',
  regionCode: 'shipping_profile[shipping][rates][][region_code]',
};

interface IProps {
  isExpeditedShippingEnabled?: boolean;
  isCombinedShippingEnabled?: boolean;
  shippingRate?: IShippingRate;
  handleRemoveShippingRate?: Function;
}

export interface IShippingRate {
  isRequired?: boolean;
  regionType?: string;
  regionCode?: string;
  regionName?: string;
  rate?: core_apimessages_Money;
  expeditedRate?: core_apimessages_Money;
  incrementalRate?: core_apimessages_Money;
  freeShippingThreshold?: core_apimessages_Money;
}

interface ValidateProps {
  incrementalRate?: string;
  expeditedRate?: string;
  standardRate?: string;
}

interface Errors {
  combinedRate?: string;
  expeditedRate?: string;
  standardRate?: string;
}

function isZero(input) {
  if (input === '' || input === undefined) { return false; }

  return Number(input) === 0;
}

function RateField({
  rateType,
  value,
  setState,
  currency,
  regionCode,
  tooltipText,
  error,
}) {
  const [hasBeenTouched, setHasBeenTouched] = React.useState(Boolean(value));
  React.useEffect(() => {
    if (value) {
      setHasBeenTouched(true);
    }
  }, [value]);

  return (
    <div className="shipping-rate-card__rate">
      <div className="shipping-rate-card__rate__input">
        <FormGroupWithInput
          inputName={INPUT_NAMES[rateType]}
          label={I18n.t(`discovery.dashboard.selling.shippingRates.rateFields.${rateType}`)}
          labelTooltipText={tooltipText}
          value={value}
          updateField={(event) => setState(event[INPUT_NAMES[rateType]])}
          currencyPrefix={Currency.get(currency).symbol}
          currencySuffix={currency}
          required
          numberOnly
          errorText={hasBeenTouched && error}
        />
      </div>
      <div className="shipping-rate-card__rate__free">
        {/* checkbox for "Set FREE" option */}
        <FormGroupWithCheckbox
          inputName={`${regionCode}_${rateType}_free`}
          label={I18n.t('discovery.dashboard.selling.shippingRates.rateFields.setFree')}
          checked={isZero(value)}
          updateField={() => setState(prevState => (isZero(prevState) ? '' : '0'))}
        />
      </div>
    </div>
  );
}

export function validateShippingRates({ standardRate, incrementalRate, expeditedRate }: ValidateProps) {
  const errorsMap: Errors = {};

  if (!standardRate) {
    errorsMap.standardRate = I18n.t(
      'discovery.dashboard.selling.shippingRates.rateFields.validationErrors.standardRequired',
    );
  }

  if (!incrementalRate) {
    errorsMap.combinedRate = I18n.t(
      'discovery.dashboard.selling.shippingRates.rateFields.validationErrors.combinedRequired',
    );
  } else if (standardRate && parseFloat(incrementalRate) > parseFloat(standardRate)) {
    errorsMap.combinedRate = I18n.t(
      'discovery.dashboard.selling.shippingRates.rateFields.validationErrors.combinedGreaterThanStandard',
    );
  }

  if (!expeditedRate) {
    errorsMap.expeditedRate = I18n.t(
      'discovery.dashboard.selling.shippingRates.rateFields.validationErrors.expeditedRequired',
    );
  } else if (standardRate && (parseFloat(expeditedRate) < parseFloat(standardRate))) {
    errorsMap.expeditedRate = I18n.t(
      'discovery.dashboard.selling.shippingRates.rateFields.validationErrors.expeditedLessThanStandard',
    );
  }

  return errorsMap;
}

export function ShippingRate(props: IProps) {
  const [standardRate, setStandardRate] = React.useState(props.shippingRate?.rate?.amount || '');
  const [incrementalRate, setIncrementalRate] = React.useState(props.shippingRate?.incrementalRate?.amount || '');
  const [expeditedRate, setExpeditedRate] = React.useState(props.shippingRate?.expeditedRate?.amount || '');
  const { currency } = props.shippingRate.rate;

  const shouldShowExpeditedRate = props.isExpeditedShippingEnabled && props.shippingRate.isRequired;
  const isEverywhereElseRegion = props.shippingRate.regionCode === EVERYWHERE_ELSE_CODE;

  const errors = validateShippingRates({ standardRate, incrementalRate, expeditedRate });

  return (
    <div className="shipping-rate-card">
      <header className="shipping-rate-card__header">
        <div className="shipping-rate-card__titleblock">
          <h4 className="shipping-rate-card__title">
            {isEverywhereElseRegion ? (
              <span className="icon-l-globe">
                {I18n.t('discovery.dashboard.selling.shippingRates.everywhereElse')}
              </span>
            ) : (
              props.shippingRate.regionName
            )}
          </h4>
          {isEverywhereElseRegion &&
            <p className="shipping-rate-card__subtitle">
              {I18n.t('discovery.dashboard.selling.shippingRates.everywhereElseTip')}
            </p>
          }
        </div>
        <div className="shipping-rate-card__remove">
          {props.shippingRate.isRequired ? (
            <span className="form-group__tag">
              {I18n.t('commons.formGroup.required')}
            </span>
          ) : (
            <button
              className="d-block button-as-link size-80 color-red"
              onClick={() => props.handleRemoveShippingRate(props.shippingRate)}
              type="button"
            >
              {I18n.t('discovery.dashboard.selling.shippingRates.rateFields.removeRate')}
            </button>
          )}
        </div>
      </header>
      <div className="shipping-rate-card__main">
        <input
          type="hidden"
          name={INPUT_NAMES.regionType}
          value={props.shippingRate.regionType}
        />
        <input
          type="hidden"
          name={INPUT_NAMES.regionCode}
          value={props.shippingRate.regionCode}
        />

        <div className="shipping-rate-card__rates">
          {/* Standard rate input */}
          <RateField
            rateType={STANDARD_RATE}
            value={standardRate}
            setState={setStandardRate}
            currency={currency}
            regionCode={props.shippingRate.regionCode}
            error={errors.standardRate}
            tooltipText={
              props.isCombinedShippingEnabled &&
              I18n.t('discovery.dashboard.selling.shippingRates.rateFields.standardShippingTooltip')
            }
          />

          {/* Combined & Save rate input */}
          {props.isCombinedShippingEnabled && (
            <RateField
              rateType={COMBINED_RATE}
              value={incrementalRate}
              setState={setIncrementalRate}
              currency={currency}
              regionCode={props.shippingRate.regionCode}
              tooltipText={I18n.t('discovery.dashboard.selling.shippingRates.rateFields.combinedShippingTooltip')}
              error={errors.combinedRate}
            />
          )}

          {/* Expdited rate input */}
          {shouldShowExpeditedRate && (
            <RateField
              rateType={EXPEDITED_RATE}
              value={expeditedRate}
              setState={setExpeditedRate}
              currency={currency}
              regionCode={props.shippingRate.regionCode}
              tooltipText=""
              error={errors.expeditedRate}
            />
          )}
        </div>
      </div>
    </div>
  );
}

export default ShippingRate;



// WEBPACK FOOTER //
// ./node_modules/@reverbdotcom/discovery-ui/src/components/shipping_profiles/shipping_rate.tsx